import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  TableContainer,
  TableSortLabel,
  Tabs,
  Tab,
  Avatar,
  InputAdornment,
  Menu,
} from '@mui/material';
import {
  Refresh,
  Delete,
  Edit,
  PersonAdd,
  Search,
  Check,
  KeyboardArrowDown,
  Group,
} from '@mui/icons-material';
import { WarehouseAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useNavigate } from 'react-router-dom';
import { ROLE_KEYS, roleColor, roleLabel } from '../components/actionables/roles';
import { eyebrow } from '../components/actionables/brand';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
import { useUrlParam, useSetUrlParams } from '../components/common/useUrlParam';
import { useToast } from '../components/common/useToast';
import RolePermissionsMatrix from '../components/admin/RolePermissionsMatrix';

// The 6 live RBAC roles (retired super-admin/viewer dropped). Labels come from the shared
// roleLabel map so this page can never diverge from the role enum.
const ASSIGNABLE_ROLES = ROLE_KEYS;

// Role keys ordered ascending by their human display label (Admin, Catalog Manager, Data & Tech,
// Photography, Warehouse Manager, Warehouse Member). Membership/validation still flows through
// ASSIGNABLE_ROLES; this copy only controls the order roles are listed in the UI pickers.
const ROLES_BY_LABEL = [...ASSIGNABLE_ROLES].sort((a, b) =>
  roleLabel(a).localeCompare(roleLabel(b), undefined, { sensitivity: 'base' }),
);

const USERS_PAGE_SIZE = DEFAULT_PAGE_SIZE;

interface UserRow {
  email: string;
  name: string;
  role?: string;
  isAdmin: boolean;
}

interface AccessRequestRow {
  _id: string;
  name: string;
  email: string;
  picture?: string | null;
  reason: string;
  requestedAt: string;
}

// A clean card shell in the RulesView.tsx language — warm-neutral border, orange top accent,
// reduced-motion-gated entrance.
const CardShell: React.FC<{ reduceMotion: boolean | null; delay?: number; children: React.ReactNode }> = ({ reduceMotion, delay = 0, children }) => (
  <motion.div
    layout={!reduceMotion}
    initial={reduceMotion ? false : { opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.28, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    <Box
      sx={{
        position: 'relative', overflow: 'hidden', borderRadius: '16px', bgcolor: '#fff',
        border: '1px solid #ececE4',
        boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.06)',
        '&::before': { content: '""', position: 'absolute', left: 0, top: 0, right: 0, height: 3, bgcolor: '#DC5829', zIndex: 1 },
      }}
    >
      {children}
    </Box>
  </motion.div>
);

// Role-coloured avatar — the ring + tint come from the shared ROLE_COLORS map so the colour is
// identical to the role's badge and matrix dot.
const RoleAvatar: React.FC<{ role?: string; name: string; src?: string | null; size?: number }> = ({ role, name, src, size = 38 }) => {
  const rc = roleColor(role || '');
  return (
    <Avatar
      src={src || undefined}
      alt={name}
      sx={{
        width: size, height: size, fontSize: size * 0.4, fontWeight: 800,
        bgcolor: rc.bg, color: rc.text,
        border: `2px solid ${rc.main}`, boxShadow: `0 0 0 3px ${rc.bg}`,
      }}
    >
      {name ? name.charAt(0).toUpperCase() : '?'}
    </Avatar>
  );
};

// Colour-coded role pill that opens a reassignment menu on click (replaces the raw row Select).
const RoleBadgeMenu: React.FC<{
  role?: string;
  disabled?: boolean;
  saving?: boolean;
  lockedToAdmin?: boolean; // self-demotion guard: only Admin stays selectable
  onChange: (role: string) => void;
}> = ({ role, disabled, saving, lockedToAdmin, onChange }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const current = ASSIGNABLE_ROLES.includes(role || '') ? (role as string) : 'warehouse-manager';
  const rc = roleColor(current);
  const open = Boolean(anchor);
  return (
    <>
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          px: 1.25, py: 0.5, borderRadius: '999px', cursor: disabled ? 'default' : 'pointer',
          bgcolor: rc.bg, color: rc.text, border: `1px solid ${rc.main}33`,
          fontSize: '0.8rem', fontWeight: 700, fontFamily: 'inherit',
          opacity: disabled ? 0.65 : 1, transition: 'filter 140ms',
          '&:hover': disabled ? {} : { filter: 'brightness(0.97)' },
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: rc.main }} />
        {roleLabel(current)}
        {saving ? <CircularProgress size={12} sx={{ color: rc.text }} /> : <KeyboardArrowDown sx={{ fontSize: 16, opacity: 0.7 }} />}
      </Box>
      <Menu anchorEl={anchor} open={open} onClose={() => setAnchor(null)}>
        {ROLES_BY_LABEL.map((r) => {
          const optColor = roleColor(r);
          const optDisabled = !!lockedToAdmin && r !== 'admin';
          return (
            <MenuItem
              key={r}
              disabled={optDisabled}
              selected={r === current}
              onClick={() => { setAnchor(null); if (r !== current) onChange(r); }}
              sx={{ gap: 1, fontSize: '0.85rem', fontWeight: 600 }}
            >
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: optColor.main }} />
              {roleLabel(r)}
              {r === current && <Check sx={{ fontSize: 16, ml: 'auto', color: '#15803d' }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

const AdminUsersList: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  // Capability-driven gates (replacing the old super-admin role-string checks). The page is
  // already route-gated on manage-users:view; edit unlocks role changes, create and the
  // access-request queue.
  const canManageUsers = usePermission('manage-users').can('edit');

  // Filters/search/sort/paging + the active tab all live in the URL (prefix "au"; the tab keeps
  // the shared "tab" key) so a refresh restores exactly where the user left off. Defaults stay
  // OUT of the URL; changing a filter/search/sort resets the page in the SAME update.
  const setUrlParams = useSetUrlParams();

  // Tabs: 'users' (default), 'roles' (Roles & Permissions matrix), and 'access-requests'
  // (folded into Users-management, now manage-users:edit — no longer super-admin-gated). Fully
  // URL-backed via the shared "tab" key; a non-permitted 'access-requests' in the URL falls back
  // to 'users' (and resolves to the real tab once perms finish loading).
  const [tabParam, setTabParam] = useUrlParam('tab', 'users');
  const activeTab: 'users' | 'roles' | 'access-requests' =
    tabParam === 'roles' ? 'roles'
      : tabParam === 'access-requests' && canManageUsers ? 'access-requests'
        : 'users';

  // Users tab client-side filtering + paging state (URL-backed).
  const [query] = useUrlParam('auq', '');
  const [roleFilter] = useUrlParam('aurole', 'all');
  const [usersPageParam, setUsersPageParam] = useUrlParam('aupage', '0');
  const usersPage = parseInt(usersPageParam, 10) || 0;
  const setUsersPage = (v: number) => setUsersPageParam(v === 0 ? '0' : String(v));
  // Client-side column sort. Default name-ascending preserves the former fixed sort behavior; the
  // Member/Role header cells toggle direction. Sort runs on the full filtered list, then the page
  // slice applies (sort-then-slice).
  const [sortKeyParam] = useUrlParam('ausort', 'name');
  const [sortDirParam] = useUrlParam('audir', 'asc');
  // Memoized so its identity is stable across renders (consumed in the sortedUsers useMemo deps).
  const usersSort = useMemo(
    () => ({ key: sortKeyParam as 'name' | 'role', dir: sortDirParam as 'asc' | 'desc' }),
    [sortKeyParam, sortDirParam],
  );

  // Search/role changes write their key (dropped at default) and reset the page in one update.
  const setQuery = (v: string) => setUrlParams({ auq: v === '' ? null : v, aupage: null });
  const setRoleFilter = (v: string) => setUrlParams({ aurole: v === 'all' ? null : v, aupage: null });

  const handleUsersSort = (key: 'name' | 'role') => {
    const nextDir = usersSort.key === key ? (usersSort.dir === 'asc' ? 'desc' : 'asc') : 'asc';
    setUrlParams({
      ausort: key === 'name' ? null : key,
      audir: nextDir === 'asc' ? null : nextDir,
      aupage: null,
    });
  };

  // Access Requests tab state.
  const [requests, setRequests] = useState<AccessRequestRow[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [requestRole, setRequestRole] = useState<Record<string, string>>({});
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingEmail, setSavingEmail] = useState<string | null>(null);

  // Edit User Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<{ email: string; name: string } | null>(null);
  const [editName, setEditName] = useState('');

  // Delete Confirmation Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await WarehouseAPI.getUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestsError(null);
    try {
      const data = await WarehouseAPI.listAccessRequests('pending');
      setRequests(data);
      setRequestRole((prev) => {
        const next = { ...prev };
        data.forEach((r) => {
          if (!next[r._id]) next[r._id] = 'warehouse-manager';
        });
        return next;
      });
    } catch (err: any) {
      setRequestsError(err?.response?.data?.message || err?.message || 'Failed to load access requests');
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  // Load requests when the Access Requests tab becomes active (manage-users:edit only).
  useEffect(() => {
    if (activeTab === 'access-requests' && canManageUsers) {
      loadRequests();
    }
  }, [activeTab, canManageUsers, loadRequests]);

  // Client-side filter (name/email search + role) applied BEFORE paging.
  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== 'all') {
        const r = ASSIGNABLE_ROLES.includes(u.role || '') ? u.role : 'warehouse-manager';
        if (r !== roleFilter) return false;
      }
      if (!q) return true;
      return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    });
  }, [users, query, roleFilter]);

  // Keep the page in range when the filter shrinks the result set.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredUsers.length / USERS_PAGE_SIZE) - 1);
    if (usersPage > maxPage) setUsersPage(maxPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers.length, usersPage]);

  // Sort the filtered set by the active column (case-insensitive). Name falls back to email when a
  // user has no name; Role compares the human role label (normalizing unknown roles exactly as the
  // filter does, so sort and filter agree). Applied AFTER search/filter and BEFORE paging.
  const sortedUsers = useMemo(() => {
    const dir = usersSort.dir === 'desc' ? -1 : 1;
    return [...filteredUsers].sort((a, b) => {
      let cmp: number;
      if (usersSort.key === 'role') {
        // Sort by the canonical ROLE_KEYS display order (Admin → Warehouse Manager →
        // Warehouse Member → Catalog Manager → Catalog Member → Data & Tech → Photography),
        // matching how the roles matrix orders roles — not alphabetically by label.
        const rank = (role: string | undefined | null) => {
          const key = ASSIGNABLE_ROLES.includes(role || '') ? (role as string) : 'warehouse-manager';
          const i = ROLE_KEYS.indexOf(key);
          return i === -1 ? ROLE_KEYS.length : i; // unknowns sort last, before dir is applied
        };
        cmp = rank(a.role) - rank(b.role);
      } else {
        cmp = (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, { sensitivity: 'base' });
      }
      return cmp * dir;
    });
  }, [filteredUsers, usersSort]);

  const pagedUsers = useMemo(
    () => sortedUsers.slice(usersPage * USERS_PAGE_SIZE, usersPage * USERS_PAGE_SIZE + USERS_PAGE_SIZE),
    [sortedUsers, usersPage],
  );

  const handleTabChange = (_e: React.SyntheticEvent, value: 'users' | 'roles' | 'access-requests') => {
    // 'users' is the default, so it drops the "tab" key from the URL.
    setTabParam(value === 'users' ? '' : value);
  };

  const handleApprove = async (id: string) => {
    setDecidingId(id);
    setRequestsError(null);
    try {
      await WarehouseAPI.approveAccessRequest(id, requestRole[id] || 'warehouse-manager');
      setRequests((prev) => prev.filter((r) => r._id !== id));
      toast.success('Access request approved and user created.', { duration: 4000 });
    } catch (err: any) {
      setRequestsError(err?.response?.data?.message || err?.message || 'Failed to approve request');
    } finally {
      setDecidingId(null);
    }
  };

  const handleReject = async (id: string) => {
    setDecidingId(id);
    setRequestsError(null);
    try {
      await WarehouseAPI.rejectAccessRequest(id);
      setRequests((prev) => prev.filter((r) => r._id !== id));
      toast.success('Access request rejected.', { duration: 4000 });
    } catch (err: any) {
      setRequestsError(err?.response?.data?.message || err?.message || 'Failed to reject request');
    } finally {
      setDecidingId(null);
    }
  };

  const changeRole = async (email: string, newRole: string) => {
    setSavingEmail(email);
    setError(null);
    try {
      // Admin self-demotion guard (UI side; the backend double-checks in PATCH /users/:email).
      const userToUpdate = users.find((u) => u.email === email);
      const isCurrentlyAdmin = userToUpdate?.role === 'admin';
      const isNewRoleAdmin = newRole === 'admin';

      if (email === currentUser?.email && isCurrentlyAdmin && !isNewRoleAdmin) {
        throw new Error('You cannot remove admin role from your own account');
      }
      await WarehouseAPI.updateUser(email, { role: newRole });
      setUsers((prev) => prev.map((u) => (u.email === email ? { ...u, role: newRole, isAdmin: newRole === 'admin' } : u)));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update user role');
    } finally {
      setSavingEmail(null);
    }
  };

  const handleEditClick = (user: { email: string; name: string }) => {
    setEditingUser(user);
    setEditName(user.name);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setSavingEmail(editingUser.email);
    try {
      await WarehouseAPI.updateUser(editingUser.email, { name: editName });
      setUsers((prev) => prev.map((u) => (u.email === editingUser.email ? { ...u, name: editName } : u)));
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update user name');
    } finally {
      setSavingEmail(null);
    }
  };

  const handleDeleteClick = (email: string) => {
    if (email === currentUser?.email) {
      setError('You cannot delete your own account');
      return;
    }
    setUserToDelete(email);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    setLoading(true);
    try {
      await WarehouseAPI.deleteUser(userToDelete);
      setUsers((prev) => prev.filter((u) => u.email !== userToDelete));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const selfDemotionBlocked = (email: string, role?: string) =>
    email === currentUser?.email && role === 'admin';

  return (
    <PageContainer>
      {/* Shared page header: icon-title + subtitle, Refresh/Create User in the actions slot. */}
      <PageHeader
        eyebrow="Administration"
        icon={<Group sx={{ color: '#DC5829', fontSize: '1.9rem' }} />}
        title="Manage Users"
        subtitle="Add or remove people, assign roles, and configure what each role can see and edit."
        actions={
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px', borderColor: '#e3ddd1', color: '#1A140F' }}>
              Refresh
            </Button>
            {canManageUsers && (
              <Button
                variant="contained" disableElevation startIcon={<PersonAdd />}
                onClick={() => navigate('/admin/create-user')}
                sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '999px', px: 2.5, bgcolor: '#1A140F', color: '#fff', '&:hover': { bgcolor: '#0F0A06' }, '&.Mui-disabled': { bgcolor: '#1A140F', color: '#fff', opacity: 0.45 } }}
              >
                Create User
              </Button>
            )}
          </>
        }
      />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 2.5, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 }, '& .Mui-selected': { color: '#DC5829 !important' }, '& .MuiTabs-indicator': { bgcolor: '#DC5829' } }}
      >
        <Tab label="Users" value="users" />
        <Tab label="Roles & Permissions" value="roles" />
        {canManageUsers && <Tab label="Access Requests" value="access-requests" />}
      </Tabs>

      {activeTab === 'roles' ? (
        <RolePermissionsMatrix />
      ) : activeTab === 'access-requests' && canManageUsers ? (
        <>
          {requestsError && <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{requestsError}</Alert>}
          <CardShell reduceMotion={reduceMotion}>
            {requestsLoading && requests.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#faf8f3' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Requested</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">No pending access requests</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      requests.map((r) => (
                        <TableRow key={r._id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar src={r.picture || undefined} alt={r.name} sx={{ width: 34, height: 34 }}>
                                {r.name ? r.name.charAt(0).toUpperCase() : '?'}
                              </Avatar>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{r.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" color="text.secondary">{r.reason}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {r.requestedAt ? new Date(r.requestedAt).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <FormControl sx={{ minWidth: 160 }}>
                              <Select
                                value={requestRole[r._id] || 'warehouse-manager'}
                                onChange={(e) => setRequestRole((prev) => ({ ...prev, [r._id]: e.target.value as string }))}
                                disabled={decidingId === r._id}
                                size="small"
                                sx={{ borderRadius: 2 }}
                              >
                                {ASSIGNABLE_ROLES.map((role) => (
                                  <MenuItem key={role} value={role}>
                                    {roleLabel(role)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                              <Button
                                variant="contained" size="small" disableElevation
                                disabled={decidingId === r._id}
                                onClick={() => handleApprove(r._id)}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px', bgcolor: '#1A140F', color: '#fff', '&:hover': { bgcolor: '#0F0A06' }, '&.Mui-disabled': { bgcolor: '#1A140F', color: '#fff', opacity: 0.45 } }}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="outlined" color="error" size="small"
                                disabled={decidingId === r._id}
                                onClick={() => handleReject(r._id)}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}
                              >
                                Reject
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardShell>
        </>
      ) : (
        <>
          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{error}</Alert>}

          {/* Compact toolbar: search + role filter + right-aligned member count. */}
          <Box
            component={motion.div}
            initial={reduceMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.75, flexWrap: 'wrap' }}
          >
            <TextField
              size="small"
              placeholder="Search by name or email"
              value={query}
              onChange={(e) => { setQuery(e.target.value); }}
              sx={{ minWidth: 260, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '999px', bgcolor: '#fff' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: '#8A8078' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <Select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); }}
                sx={{ borderRadius: '999px', bgcolor: '#fff' }}
                renderValue={(val) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {val !== 'all' && <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: roleColor(val as string).main }} />}
                    {val === 'all' ? 'All roles' : roleLabel(val as string)}
                  </Box>
                )}
              >
                <MenuItem value="all">All roles</MenuItem>
                {ROLES_BY_LABEL.map((role) => (
                  <MenuItem key={role} value={role} sx={{ gap: 1 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: roleColor(role).main }} />
                    {roleLabel(role)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ ml: 'auto' }}>
              <Typography sx={{ ...eyebrow, color: '#8A8078' }}>
                {filteredUsers.length} {filteredUsers.length === 1 ? 'member' : 'members'}
              </Typography>
            </Box>
          </Box>

          <CardShell reduceMotion={reduceMotion}>
            {loading && users.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" sx={{ '& td, & th': { borderColor: '#f4f1ea' } }}>
                  <TableHead sx={{ bgcolor: '#faf8f3' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }} sortDirection={usersSort.key === 'name' ? usersSort.dir : false}>
                        <TableSortLabel
                          active={usersSort.key === 'name'}
                          direction={usersSort.key === 'name' ? usersSort.dir : 'asc'}
                          onClick={() => handleUsersSort('name')}
                        >
                          Member
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} sortDirection={usersSort.key === 'role' ? usersSort.dir : false}>
                        <TableSortLabel
                          active={usersSort.key === 'role'}
                          direction={usersSort.key === 'role' ? usersSort.dir : 'asc'}
                          onClick={() => handleUsersSort('role')}
                        >
                          Role
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 110 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">
                            {users.length === 0 ? 'No users found' : 'No members match your filters'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedUsers.map((u, i) => (
                        <TableRow
                          key={u.email}
                          component={motion.tr}
                          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.22, delay: Math.min(i * 0.025, 0.25), ease: [0.16, 1, 0.3, 1] }}
                          sx={{
                            '&:hover': { bgcolor: '#faf8f3' },
                          }}
                        >
                          <TableCell sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <RoleAvatar role={u.role} name={u.name} />
                              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.25, color: '#1A140F' }} noWrap>{u.name}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{u.email}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Tooltip
                              title={selfDemotionBlocked(u.email, u.role) ? 'You cannot remove your own Admin role' : ''}
                              disableHoverListener={!selfDemotionBlocked(u.email, u.role)}
                            >
                              <span>
                                <RoleBadgeMenu
                                  role={u.role}
                                  disabled={savingEmail === u.email || !canManageUsers}
                                  saving={savingEmail === u.email}
                                  lockedToAdmin={selfDemotionBlocked(u.email, u.role)}
                                  onChange={(r) => changeRole(u.email, r)}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1 }}>
                            <Box
                              className="row-actions"
                              sx={{
                                display: 'flex', justifyContent: 'flex-end', gap: 0.5,
                                opacity: 1,
                              }}
                            >
                              <Tooltip title="Edit name">
                                <span>
                                  <IconButton
                                    onClick={() => handleEditClick(u)}
                                    sx={{ color: '#DC5829' }}
                                    size="small"
                                    disabled={savingEmail === u.email || !canManageUsers}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete user">
                                <span>
                                  <IconButton
                                    onClick={() => handleDeleteClick(u.email)}
                                    color="error"
                                    size="small"
                                    disabled={savingEmail === u.email || u.email === currentUser?.email || !canManageUsers}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePaginationBar
              page={usersPage}
              total={filteredUsers.length}
              rowsPerPage={USERS_PAGE_SIZE}
              onPageChange={setUsersPage}
            />
          </CardShell>
        </>
      )}

      {/* Edit Name Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Edit User Name</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Editing: {editingUser?.email}
            </Typography>
            <TextField
              label="Full Name"
              fullWidth
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={!editName || editName === editingUser?.name || !!savingEmail}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete /> Delete User?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AdminUsersList;
